import { io, Socket } from 'socket.io-client'
import { getAuthToken } from 'helpers/auth'
import { ClientToServerEvents, JoinRoomResult, ServerToClientEvents } from 'hooks/socketTypes'

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>

// The credential the current connection was opened with. The websocket server
// authorises every room join with the credential from the handshake, so when
// the session changes the socket has to reconnect to present the new one.
let handshakeCredential: string | null = null

const currentCredential = (): string | null => getAuthToken() ?? null

/**
 * Socket.IO `auth` option. Socket.IO calls it on every (re)connection, so each
 * handshake carries the session's current `Authorization` value, or null for
 * an anonymous visitor (public hubs, shared views and embeds).
 */
export const socketAuth = (cb: (data: { token: string | null }) => void): void => {
  handshakeCredential = currentCredential()
  cb({ token: handshakeCredential })
}

export const createAppSocket = (host: string): AppSocket =>
  io(host, {
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    timeout: 10000,
    auth: socketAuth
  })

/**
 * Reconnect when the session's credential differs from the one the connection
 * was opened with (sign-in, token refresh, sign-out, masquerade), so rooms are
 * authorised as the current user and rooms joined as someone else are dropped.
 * The `connect` handlers that join rooms re-join them. Returns whether it
 * reconnected.
 */
export const reconnectIfCredentialChanged = (socket: AppSocket): boolean => {
  if (!socket.connected || currentCredential() === handshakeCredential) return false
  socket.disconnect().connect()
  return true
}

// Back-off for a join the server could not check (its API call failed). After
// the last step it keeps retrying at that interval for as long as the room is
// wanted, so an API outage of any length doesn't leave a tab without live updates.
export const JOIN_RETRY_DELAYS_MS = [2_000, 10_000, 30_000]

/**
 * Join a room and retry while the server answers `unavailable`. A refused join
 * is not retried; an older server that never acks is left as it is (it joined
 * without checking). Returns a function that cancels pending retries; call it
 * before leaving the room.
 */
export const joinSocketRoom = (socket: AppSocket, room: string): (() => void) => {
  let cancelled = false
  let retryTimer: ReturnType<typeof setTimeout> | undefined

  const attempt = (retry: number): void => {
    socket.emit('join', room, (result: JoinRoomResult) => {
      if (cancelled || result.ok || result.reason !== 'unavailable') return
      const delay = JOIN_RETRY_DELAYS_MS[Math.min(retry, JOIN_RETRY_DELAYS_MS.length - 1)]
      retryTimer = setTimeout(() => {
        if (!cancelled && socket.connected) attempt(retry + 1)
      }, delay)
    })
  }

  attempt(0)

  return () => {
    cancelled = true
    clearTimeout(retryTimer)
  }
}

/**
 * Stay in `room` for as long as wanted: join now if connected, and again on every connect, since a new
 * connection starts in no rooms. A reconnect may have missed the room's events, so `onReconnect` runs on
 * each connect after the first. Returns a function that cancels pending retries and leaves the room.
 */
export const subscribeSocketRoom = (socket: AppSocket, room: string, onReconnect: () => void): (() => void) => {
  let cancelJoin = () => {}
  const joinRoom = () => {
    cancelJoin()
    cancelJoin = joinSocketRoom(socket, room)
  }

  let connectedBefore = socket.connected
  if (socket.connected) joinRoom()

  const onConnect = () => {
    joinRoom()
    if (connectedBefore) onReconnect()
    connectedBefore = true
  }
  socket.on('connect', onConnect)

  return () => {
    cancelJoin()
    socket.emit('leave', room)
    socket.off('connect', onConnect)
  }
}
